import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FlashIcon as Zap, Tick01Icon as Check, ArrowRight01Icon as ChevronRight, ComputerIcon as Monitor, Mic01Icon as Mic, MicOff01Icon as MicOff, Delete02Icon as Trash2, RotateLeft01Icon as RotateCcw, Cancel01Icon as X, Camera01Icon as Camera, Folder01Icon as Folder, TestTube01Icon as FlaskConical, Shield01Icon as ShieldCheck, CircleUnlock01Icon as Unlock, PlayIcon as Play, PauseIcon as Pause, ArrowDown01Icon as ChevronDown } from 'hugeicons-react'
import { useRecorder } from '@/hooks/useRecorder'
import { useProjectStore } from '@/store/project-store'
import { useNavigate } from 'react-router-dom'
import ProjectSidebar from '@/components/editor/ProjectSidebar'
import { screenRecorder } from '@/lib/recording' // Import singleton for stream access
import MonitorRecorderIcon from '@/components/icons/MonitorRecorderIcon'
import FaqModal from '@/components/modals/FaqModal'
import FeedbackModal, { type FeedbackTabType } from '@/components/modals/FeedbackModal'
import DonateModal from '@/components/modals/DonateModal'

/* Hand-drawn loopy scribble arrow component - vector touch & point */
function ScribbleArrow() {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8, x: -4, y: -4 }}
            animate={{ opacity: 1, scale: 1, x: 0, y: [0, 5, 0] }}
            exit={{ opacity: 0, scale: 0.8, x: -4, y: -4 }}
            transition={{
                y: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' },
                duration: 0.22
            }}
            className="absolute left-[calc(100%-6px)] top-3 pointer-events-none z-50 hidden md:block"
        >
            <svg
                width="64"
                height="92"
                viewBox="0 0 64 92"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-white filter drop-shadow-[0_0_12px_rgba(96,165,250,0.85)]"
            >
                {/* Spiral loopy path touching button edge and curving down */}
                <motion.path
                    d="M 4 20 C 20 2, 54 10, 42 30 C 30 46, 14 28, 32 46 C 46 60, 44 76, 40 88"
                    stroke="currentColor"
                    strokeWidth="3.6"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                />
                {/* Arrowhead pointing down to video frame */}
                <motion.path
                    d="M 26 78 L 40 88 L 50 76"
                    stroke="currentColor"
                    strokeWidth="3.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.2, delay: 0.3, ease: 'easeOut' }}
                />
            </svg>
        </motion.div>
    )
}

/* --- Record Button & Try Demo Button --- */
function RecordButton({ onStart, onTryDemo }: { onStart: () => void, onTryDemo: () => void }) {
    const [isHoveringDemo, setIsHoveringDemo] = useState(false)

    return (
        <div className="flex flex-col md:flex-row items-center gap-4 relative">
            <motion.button
                onClick={onStart}
                className="relative group flex items-center gap-4 px-8 py-5 rounded-2xl bg-brand-500 hover:bg-brand-400 text-white font-semibold text-lg shadow-lg transition-all duration-200 overflow-hidden"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                <div className="relative flex items-center gap-3">
                    <MonitorRecorderIcon size={24} color="#FFFFFF" animate={true} />
                    Start Recording
                </div>
                <ChevronRight className="relative w-5 h-5 opacity-60 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <div className="relative">
                <motion.button
                    onClick={onTryDemo}
                    onMouseEnter={() => setIsHoveringDemo(true)}
                    onMouseLeave={() => setIsHoveringDemo(false)}
                    className="flex items-center gap-3 px-8 py-5 rounded-2xl bg-white/5 border border-white/10 text-white font-medium text-lg hover:bg-white/10 hover:border-brand-500/30 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    <FlaskConical className="w-5 h-5 text-brand-400" />
                    Try with Demo
                </motion.button>

                <AnimatePresence>
                    {isHoveringDemo && <ScribbleArrow />}
                </AnimatePresence>
            </div>
        </div>
    )
}

function PreRecordingModal({ isOpen, onClose, onStart }: { isOpen: boolean, onClose: () => void, onStart: (options: any) => void }) {
    const [mode, setMode] = useState<'screen' | 'camera'>('screen')
    const [micEnabled, setMicEnabled] = useState(false)
    const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([])
    const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])
    const [selectedMic, setSelectedMic] = useState<string>('')
    const [selectedCamera, setSelectedCamera] = useState<string>('')
    const [previewStream, setPreviewStream] = useState<MediaStream | null>(null)
    const [micDropdownOpen, setMicDropdownOpen] = useState(false)
    const [camDropdownOpen, setCamDropdownOpen] = useState(false)
    const previewVideoRef = useRef<HTMLVideoElement>(null)

    // Load devices and handle permissions
    const loadDevices = async () => {
        try {
            // Only request what we need to minimize prompts
            if (micEnabled || mode === 'camera') {
                await navigator.mediaDevices.getUserMedia({
                    audio: micEnabled,
                    video: mode === 'camera'
                }).then(stream => {
                    // Stop tracks immediately as we just wanted permissions for labels
                    stream.getTracks().forEach(t => t.stop())
                }).catch(err => {
                    console.warn("Permission denied or no device:", err)
                })
            }

            const devices = await navigator.mediaDevices.enumerateDevices()

            const mics = devices.filter(d => d.kind === 'audioinput')
            const cams = devices.filter(d => d.kind === 'videoinput')

            setMicrophones(mics)
            setCameras(cams)

            if (mics.length > 0 && !selectedMic) setSelectedMic(mics[0].deviceId)
            if (cams.length > 0 && !selectedCamera) setSelectedCamera(cams[0].deviceId)

        } catch (err) {
            console.error("Failed to load devices:", err)
        }
    }

    // Handle camera preview
    useEffect(() => {
        let stream: MediaStream | null = null

        const startPreview = async () => {
            if (mode === 'camera' && isOpen) {
                try {
                    stream = await navigator.mediaDevices.getUserMedia({
                        video: selectedCamera ? { deviceId: { exact: selectedCamera } } : true
                    })
                    setPreviewStream(stream)
                    if (previewVideoRef.current) {
                        previewVideoRef.current.srcObject = stream
                    }
                } catch (err) {
                    console.error("Failed to start preview:", err)
                }
            } else {
                setPreviewStream(null)
            }
        }

        startPreview()

        return () => {
            if (stream) {
                stream.getTracks().forEach(t => t.stop())
            }
        }
    }, [mode, selectedCamera, isOpen])

    useEffect(() => {
        if (isOpen) {
            loadDevices()
        }
    }, [isOpen, mode, micEnabled])

    if (!isOpen) return null

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-[#0A0A0C] border border-white/10 rounded-3xl shadow-2xl overflow-hidden w-full max-w-lg my-auto"
                >
                    <div className="p-6 sm:p-7">
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-display font-semibold text-white mb-1">Recording Setup</h2>
                                <p className="text-sm text-txt-dim">Configure audio & camera, then select a page to record</p>
                            </div>
                            <button onClick={onClose} className="p-2 -mr-1 -mt-1 hover:bg-white/10 rounded-full text-txt-dim hover:text-white transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3.5 mb-6">
                            <button
                                onClick={() => setMode('screen')}
                                className={`flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all ${mode === 'screen' ? 'bg-brand-500/10 border-brand-500 text-white shadow-lg shadow-brand-500/10' : 'bg-white/5 border-transparent text-txt-secondary hover:bg-white/10'}`}
                            >
                                <div className={`p-3 rounded-xl ${mode === 'screen' ? 'bg-brand-500 text-white' : 'bg-white/10 text-txt-dim'}`}>
                                    <Monitor size={26} />
                                </div>
                                <span className="text-sm font-semibold">Screen Only</span>
                            </button>
                            <button
                                onClick={() => setMode('camera')}
                                className={`flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all ${mode === 'camera' ? 'bg-brand-500/10 border-brand-500 text-white shadow-lg shadow-brand-500/10' : 'bg-white/5 border-transparent text-txt-secondary hover:bg-white/10'}`}
                            >
                                <div className={`p-3 rounded-xl ${mode === 'camera' ? 'bg-brand-500 text-white' : 'bg-white/10 text-txt-dim'}`}>
                                    <Camera size={26} />
                                </div>
                                <span className="text-sm font-semibold">Screen + Cam</span>
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Microphone Section */}
                            <div className="space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-sm font-medium text-white">
                                        {micEnabled ? <Mic size={18} className="text-brand-400" /> : <MicOff size={18} className="text-txt-dim" />}
                                        Microphone (Muted by default)
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setMicEnabled(!micEnabled)}
                                        className={`w-10 h-5 rounded-full transition-colors relative flex items-center ${micEnabled ? 'bg-brand-500' : 'bg-white/15'}`}
                                    >
                                        <div className={`w-3.5 h-3.5 rounded-full bg-white transition-transform ${micEnabled ? 'translate-x-[22px]' : 'translate-x-[4px]'}`} />
                                    </button>
                                </div>
                                {micEnabled && microphones.length > 0 ? (
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setMicDropdownOpen(!micDropdownOpen)}
                                            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white flex items-center justify-between hover:bg-black/60 transition-colors focus:border-brand-500 text-left outline-none cursor-pointer"
                                        >
                                            <span className="truncate">
                                                {microphones.find(m => m.deviceId === selectedMic)?.label || microphones[0]?.label || `Microphone ${selectedMic.slice(0, 4)}...`}
                                            </span>
                                            <ChevronDown size={16} className="text-txt-dim shrink-0 ml-2" />
                                        </button>
                                        {micDropdownOpen && (
                                            <>
                                                <div className="fixed inset-0 z-40" onClick={() => setMicDropdownOpen(false)} />
                                                <div className="absolute left-0 right-0 mt-1.5 z-50 bg-[#0A0A0C] border border-white/10 rounded-xl shadow-2xl overflow-hidden py-1 max-h-48 overflow-y-auto">
                                                    {microphones.map(mic => (
                                                        <button
                                                            key={mic.deviceId}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedMic(mic.deviceId)
                                                                setMicDropdownOpen(false)
                                                            }}
                                                            className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between transition-colors hover:bg-white/5 ${selectedMic === mic.deviceId ? 'text-brand-400 bg-brand-500/5 font-medium' : 'text-txt-secondary hover:text-white'}`}
                                                        >
                                                            <span className="truncate">{mic.label || `Microphone ${mic.deviceId.slice(0, 4)}...`}</span>
                                                            {selectedMic === mic.deviceId && <Check size={14} className="text-brand-400 shrink-0 ml-2" />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : micEnabled && (
                                    <div className="text-[10px] text-danger-400/80 bg-danger-500/5 border border-danger-500/10 rounded-lg p-2">
                                        No microphone detected. Please check your permissions or connections.
                                    </div>
                                )}
                            </div>

                            {/* System Sound Tip Box */}
                            <div className="flex items-start gap-3 p-3.5 bg-brand-500/10 border border-brand-500/20 rounded-xl text-xs text-brand-300/90 leading-relaxed">
                                <span className="text-base shrink-0 mt-0.5">🔊</span>
                                <div>
                                    <strong className="text-white font-semibold">Tip for System / Tab Audio:</strong> When you click below, the browser will ask which page or screen to share. Be sure to check <span className="underline font-bold text-white">"Share audio"</span> in the bottom-left of that browser picker!
                                </div>
                            </div>

                            {/* Camera Section */}
                            {mode === 'camera' && (
                                <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-500 pt-1">
                                    <div className="flex items-center gap-2 text-sm font-medium text-white">
                                        <Camera size={18} className="text-brand-400" />
                                        Camera Source
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="aspect-video bg-black rounded-xl overflow-hidden border border-white/10 relative">
                                            <video
                                                ref={previewVideoRef}
                                                autoPlay
                                                muted
                                                playsInline
                                                className="w-full h-full object-cover"
                                            />
                                            {!previewStream && (
                                                <div className="absolute inset-0 flex items-center justify-center text-[10px] text-txt-dim text-center p-4">
                                                    Allow camera access to see preview
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col justify-center gap-3">
                                            {cameras.length > 0 ? (
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setCamDropdownOpen(!camDropdownOpen)}
                                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white flex items-center justify-between hover:bg-black/60 transition-colors focus:border-brand-500 text-left outline-none cursor-pointer"
                                                    >
                                                        <span className="truncate">
                                                            {cameras.find(c => c.deviceId === selectedCamera)?.label || cameras[0]?.label || `Camera ${selectedCamera.slice(0, 4)}...`}
                                                        </span>
                                                        <ChevronDown size={16} className="text-txt-dim shrink-0 ml-2" />
                                                    </button>
                                                    {camDropdownOpen && (
                                                        <>
                                                            <div className="fixed inset-0 z-40" onClick={() => setCamDropdownOpen(false)} />
                                                            <div className="absolute left-0 right-0 mt-1.5 z-50 bg-[#0A0A0C] border border-white/10 rounded-xl shadow-2xl overflow-hidden py-1 max-h-48 overflow-y-auto">
                                                                {cameras.map(cam => (
                                                                    <button
                                                                        key={cam.deviceId}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setSelectedCamera(cam.deviceId)
                                                                            setCamDropdownOpen(false)
                                                                        }}
                                                                        className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between transition-colors hover:bg-white/5 ${selectedCamera === cam.deviceId ? 'text-brand-400 bg-brand-500/5 font-medium' : 'text-txt-secondary hover:text-white'}`}
                                                                    >
                                                                        <span className="truncate">{cam.label || `Camera ${cam.deviceId.slice(0, 4)}...`}</span>
                                                                        {selectedCamera === cam.deviceId && <Check size={14} className="text-brand-400 shrink-0 ml-2" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-[10px] text-danger-400/80 bg-danger-500/5 border border-danger-500/10 rounded-lg p-3">
                                                    No camera detected.
                                                </div>
                                            )}
                                            <p className="text-[10px] text-txt-dim leading-relaxed">
                                                The camera overlay will be recorded as a separate layer that you can move in the editor.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-7">
                            <button
                                onClick={() => onStart({
                                    screen: true,
                                    audio: true,
                                    microphone: micEnabled,
                                    camera: mode === 'camera',
                                    micDeviceId: selectedMic,
                                    cameraDeviceId: selectedCamera
                                })}
                                className="w-full py-3.5 px-6 bg-brand-500 hover:bg-brand-400 active:bg-brand-600 text-white rounded-xl font-bold text-sm sm:text-base shadow-lg shadow-brand-500/25 transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-3 cursor-pointer"
                            >
                                <MonitorRecorderIcon size={22} color="#FFFFFF" animate={true} />
                                <span>Choose Screen & Start Recording</span>
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
function RecordingOverlay({ status, duration, onStop, onRetake, onDelete, toggleMic, isPiP, onPause, onResume }: any) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const cameraVideoRef = useRef<HTMLVideoElement>(null)
    const [isMicOn, setIsMicOn] = useState(true)

    useEffect(() => {
        if (status === 'recording' || status === 'paused') {
            const stream = screenRecorder.getStream()
            if (videoRef.current && stream) {
                videoRef.current.srcObject = stream
            }

            const camStream = screenRecorder.getCameraStream()
            if (cameraVideoRef.current && camStream) {
                cameraVideoRef.current.srcObject = camStream
            }
        }
    }, [status, isPiP])

    const handleToggleMic = () => {
        const newState = !isMicOn
        setIsMicOn(newState)
        toggleMic(newState)
    }

    if (status !== 'recording' && status !== 'paused') return null

    const containerClasses = isPiP
        ? "w-full h-full flex flex-col bg-[#1C1C1E] overflow-hidden"
        : "fixed bottom-6 left-6 z-50 flex flex-col items-center bg-[#1C1C1E] border border-white/10 rounded-2xl shadow-2xl overflow-hidden w-[320px]"

    const Content = (
        <div className={containerClasses}>
            <div className="w-full h-10 flex items-center justify-between px-4 bg-white/[0.03] border-b border-white/5 shrink-0 select-none">
                <div className="flex items-center gap-2.5">
                    <div className={`w-2 h-2 rounded-full ${status === 'paused' ? 'bg-amber-500' : 'bg-danger-500 animate-pulse shadow-[0_0_8px_#ef4444]'}`} />
                    <span className="text-[11px] font-bold text-white/90 tracking-widest uppercase">{status === 'paused' ? 'Paused' : 'Recording'}</span>
                </div>
                <div className="flex items-center gap-1.5 bg-black/40 px-2 py-0.5 rounded-md border border-white/5">
                    <span className="text-xs font-mono font-medium text-brand-400 tabular-nums">
                        {new Date(duration * 1000).toISOString().substr(14, 5)}
                    </span>
                </div>
            </div>

            <div className="w-full flex-1 bg-black relative group min-h-0">
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                />
                {screenRecorder.getCameraStream() && (
                    <div className="absolute bottom-3 right-3 w-20 h-20 rounded-full border-2 border-brand-500 overflow-hidden bg-surface-900 shadow-xl z-10">
                        <video
                            ref={cameraVideoRef}
                            autoPlay
                            muted
                            playsInline
                            className="w-full h-full object-cover px-[1px]"
                        />
                    </div>
                )}
            </div>

            <div className="w-full p-4 flex items-center justify-between gap-3 shrink-0 bg-[#1C1C1E] border-t border-white/5">
                <div className="flex items-center gap-1">
                    <button
                        onClick={onDelete}
                        className="p-2.5 hover:bg-danger-500/10 rounded-xl text-txt-dim hover:text-danger-400 transition-all active:scale-95 cursor-pointer"
                        title="Delete Recording"
                    >
                        <Trash2 size={20} />
                    </button>

                    <button
                        onClick={onRetake}
                        className="p-2.5 hover:bg-white/5 rounded-xl text-txt-dim hover:text-white transition-all active:scale-95 cursor-pointer"
                        title="Retake (Restart)"
                    >
                        <RotateCcw size={20} />
                    </button>

                    <div className="w-px h-6 bg-white/10 mx-1" />

                    <button
                        onClick={status === 'paused' ? onResume : onPause}
                        className="p-2.5 hover:bg-white/5 rounded-xl text-txt-dim hover:text-white transition-all active:scale-95 cursor-pointer"
                        title={status === 'paused' ? 'Resume Recording' : 'Pause Recording'}
                    >
                        {status === 'paused' ? <Play size={20} /> : <Pause size={20} />}
                    </button>

                    <button
                        onClick={handleToggleMic}
                        className={`p-2.5 rounded-xl transition-all active:scale-95 cursor-pointer ${!isMicOn ? 'bg-danger-500/15 text-danger-500' : 'hover:bg-white/5 text-txt-dim hover:text-white'}`}
                        title="Toggle Microphone"
                    >
                        {isMicOn ? <Mic size={20} /> : <MicOff size={20} />}
                    </button>
                </div>

                <button
                    onClick={onStop}
                    className="px-6 py-2.5 bg-danger-500 hover:bg-danger-600 text-white rounded-xl text-sm font-bold flex items-center gap-2.5 transition-all shadow-lg shadow-danger-500/20 active:scale-[0.97] cursor-pointer"
                >
                    <div className="w-2.5 h-2.5 bg-white rounded-sm animate-pulse" />
                    Stop
                </button>
            </div>
        </div>
    )

    if (isPiP) return Content

    return (
        <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
        >
            {Content}
        </motion.div>
    )
}


export default function HomePage() {
    const { startRecording, status, duration, stopRecording, cancelRecording, toggleMic, pauseRecording, resumeRecording } = useRecorder()
    const { setIsSidebarOpen } = useProjectStore()
    const [showModal, setShowModal] = useState(false)
    const [pipWindow, setPipWindow] = useState<Window | null>(null)
    const [isFaqOpen, setIsFaqOpen] = useState(false)
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)
    const [feedbackTab, setFeedbackTab] = useState<FeedbackTabType>('feedback')
    const [isDonateOpen, setIsDonateOpen] = useState(false)
    const navigate = useNavigate()
    const lastOptionsRef = useRef<any>(null)

    // Handle Document Picture-in-Picture
    useEffect(() => {
        // Close PiP window when component unmounts to prevent it from hanging
        return () => {
            if (pipWindow) {
                pipWindow.close()
            }
        }
    }, [pipWindow])

    // Handle Document Picture-in-Picture lifecycle
    useEffect(() => {
        if (status === 'recording' && !pipWindow) {
            const startPiP = async () => {
                if ('documentPictureInPicture' in window) {
                    try {
                        const pip = await (window as any).documentPictureInPicture.requestWindow({
                            width: 480,
                            height: 300,
                        })

                        // Copy styles
                        Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style')).forEach((el) => {
                            pip.document.head.appendChild(el.cloneNode(true));
                        });

                        // Set body style
                        pip.document.body.style.margin = '0';
                        pip.document.body.style.backgroundColor = '#1C1C1E';
                        pip.document.body.style.height = '100vh';
                        pip.document.body.style.overflow = 'hidden';

                        setPipWindow(pip)

                        pip.addEventListener('pagehide', () => {
                            setPipWindow(null)
                        })
                    } catch (err) {
                        console.error("Failed to open PiP window:", err)
                    }
                }
            }
            startPiP()
        } else if (status !== 'recording' && status !== 'paused' && pipWindow) {
            // Close PiP when recording stops or pauses
            pipWindow.close()
            setPipWindow(null)
        }
    }, [status, pipWindow])

    const handleStart = async (options: any) => {
        try {
            lastOptionsRef.current = options
            setShowModal(false)
            await startRecording(options)
        } catch (error: any) {
            if (error.name !== 'NotAllowedError' && error.name !== 'PermissionDeniedError') {
                console.error("Failed to start recording:", error)
            }
        }
    }

    const handleRetake = async () => {
        const options = lastOptionsRef.current
        await cancelRecording()
        if (options) {
            setTimeout(async () => {
                try {
                    await startRecording(options)
                } catch (error) {
                    console.error("Failed to auto-restart recording:", error)
                }
            }, 250)
        }
    }

    const handleTryDemo = () => {
        navigate('/demo')
    }

    return (
        <div className="min-h-screen flex flex-col items-center pt-32 pb-20 relative overflow-x-hidden selection:bg-brand-500/30">
            {/* Header */}
            <div className="absolute top-0 w-full h-20 px-8 flex items-center justify-between z-40">
                <div className="flex items-center gap-3">
                    <img src="/logo.svg" alt="ScreenRecord.ing" className="w-9 h-9" />
                    <span className="text-xl font-display font-medium text-white tracking-tight">ScreenRecord<span className="text-brand-400">.ing</span></span>
                </div>
                <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 transition-all hover:scale-105 active:scale-95 backdrop-blur-md"
                >
                    <Folder size={16} className="text-brand-400" />
                    My Projects
                </button>
            </div>

            {pipWindow
                ? createPortal(
                    <RecordingOverlay
                        status={status}
                        duration={duration}
                        onStop={stopRecording}
                        onRetake={handleRetake}
                        onDelete={cancelRecording}
                        toggleMic={toggleMic}
                        isPiP={true}
                        onPause={pauseRecording}
                        onResume={resumeRecording}
                    />,
                    pipWindow.document.body
                )
                : <RecordingOverlay
                    status={status}
                    duration={duration}
                    onStop={stopRecording}
                    onRetake={handleRetake}
                    onDelete={cancelRecording}
                    toggleMic={toggleMic}
                    isPiP={false}
                    onPause={pauseRecording}
                    onResume={resumeRecording}
                />
            }

            <PreRecordingModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onStart={handleStart}
            />

            {/* Ambient Background — screen.studio-style single drifting blob + Arc-style noise */}
            <div className="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[900px] h-[700px] rounded-full bg-brand-500/[0.12] blur-[140px] pointer-events-none -z-10 animate-blob-drift" />
            <div className="absolute bottom-0 right-[-200px] w-[600px] h-[500px] rounded-full bg-brand-600/[0.06] blur-[120px] pointer-events-none -z-10" />
            
            {/* Faded Grid & Noise — Premium look */}
            <div className="grid-bg -z-10" />
            <div className="noise-bg -z-10" />
            
            {/* Fine noise grain — Arc browser technique, mix-blend-mode overlay */}
            <div className="noise-overlay z-0" />

            {/* Hero Playground Wrapper */}
            <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 flex flex-col items-center">
                {/* 🎈 Floating Draggable Stickers in Outer Left & Right Margins */}
                <FloatingSticker
                    icon={<ShieldCheck className="w-5 h-5" />}
                    text="100% Private"
                    delay={0}
                    initialRotation={-4}
                    className="absolute left-0 lg:left-4 xl:left-8 top-8 hidden md:flex z-30"
                />
                <FloatingSticker
                    icon={<Check className="w-5 h-5" />}
                    text="No Watermarks"
                    delay={0.9}
                    initialRotation={3}
                    className="absolute left-2 lg:left-8 xl:left-14 top-64 hidden md:flex z-30"
                />
                <FloatingSticker
                    icon={<Zap className="w-5 h-5" />}
                    text="No Install"
                    delay={1.6}
                    initialRotation={4}
                    className="absolute right-0 lg:right-4 xl:right-8 top-8 hidden md:flex z-30"
                />
                <FloatingSticker
                    icon={<Unlock className="w-5 h-5" />}
                    text="Open Access"
                    delay={2.3}
                    initialRotation={-3}
                    className="absolute right-2 lg:right-8 xl:right-14 top-64 hidden md:flex z-30"
                />

                {/* Hero Content */}
                <main className="relative z-10 text-center max-w-4xl px-6 flex flex-col items-center">

                    {/* Badge */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-6 backdrop-blur"
                    >
                        <span className="flex h-2 w-2 rounded-full bg-brand-400"></span>
                        <span className="text-xs font-medium text-brand-100/80 tracking-wide uppercase">Browser-based • 4K Ready</span>
                    </motion.div>

                    {/* Tagline - Above Headline */}
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="text-sm font-medium text-white/30 tracking-[0.3em] uppercase mb-4"
                    >
                        Record. Refine. Release.
                    </motion.p>

                    {/* Headline */}
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-5xl md:text-7xl font-display font-medium tracking-display text-white mb-6"
                    >
                        The Screen Recorder That <br />
                        Just Works <span className="text-brand-400">Your Way.</span>
                    </motion.h1>

                    {/* Subheadline */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="mb-12"
                    >
                        <p className="text-xl text-txt-secondary max-w-2xl mx-auto leading-relaxed">
                            The fastest way to record, edit, and share. Just open the site, hit record,
                            and download your video immediately. No waiting, no lag, no friction—just your ideas in motion.
                        </p>
                    </motion.div>

                    {/* CTA */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 }}
                        className="mb-16"
                    >
                        <RecordButton
                            onStart={() => setShowModal(true)}
                            onTryDemo={handleTryDemo}
                        />
                    </motion.div>

                    {/* Mobile Floating Stickers Row */}
                    <div className="grid grid-cols-2 gap-4 md:hidden mb-12 w-full">
                        <FloatingSticker
                            icon={<ShieldCheck className="w-5 h-5" />}
                            text="100% Private"
                            delay={0}
                            initialRotation={-2}
                        />
                        <FloatingSticker
                            icon={<Check className="w-5 h-5" />}
                            text="No Watermarks"
                            delay={0.8}
                            initialRotation={2}
                        />
                        <FloatingSticker
                            icon={<Zap className="w-5 h-5" />}
                            text="No Install"
                            delay={1.5}
                            initialRotation={-1.5}
                        />
                        <FloatingSticker
                            icon={<Unlock className="w-5 h-5" />}
                            text="Open Access"
                            delay={2.2}
                            initialRotation={3}
                        />
                    </div>

                    {/* Featured Video Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                        className="relative w-full max-w-[1000px] px-4"
                    >
                        {/* Mac-style Window Frame */}
                        <div className="relative rounded-3xl border border-white/10 bg-surface-900/40 backdrop-blur-xl overflow-hidden shadow-[0_32px_120px_-20px_rgba(0,0,0,0.8)]">
                            {/* Traffic Lights (Visual only) */}
                            <div className="h-10 border-b border-white/5 bg-white/2 flex items-center px-4 gap-2">
                                <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                                <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                                <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                            </div>

                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full aspect-video object-cover"
                            >
                                <source src="/Demo-video-Feature.mp4" type="video/mp4" />
                            </video>

                        {/* Badge Overlay */}
                        <div className="absolute bottom-8 right-8 flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/60 backdrop-blur-md border border-white/10">
                            <Zap size={10} className="text-brand-400 fill-brand-400" />
                            <span className="text-[10px] font-bold text-white uppercase tracking-widest">Recorded in-browser</span>
                        </div>
                    </div>
                </motion.div>

                {/* Products Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.75 }}
                    className="mt-20 w-full"
                >
                    <p className="text-[11px] font-medium text-white/20 tracking-[0.3em] uppercase mb-6">Available On</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Web App */}
                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] text-left">
                            <div className="w-9 h-9 rounded-xl bg-brand-500/15 border border-brand-500/20 flex items-center justify-center shrink-0">
                                <Monitor size={16} className="text-brand-400" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-white">Web App</span>
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-brand-500/15 text-brand-400 font-medium">LIVE</span>
                                </div>
                                <p className="text-[11px] text-txt-dim leading-relaxed">Record, edit, export — right in your browser. No install required.</p>
                            </div>
                        </div>

                        {/* Chrome Extension */}
                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] text-left">
                            <div className="w-9 h-9 rounded-xl bg-success-500/10 border border-success-500/15 flex items-center justify-center shrink-0">
                                <span className="text-base leading-none">🧩</span>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-white">Chrome Extension</span>
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-warning-500/15 text-warning-400 font-medium">BETA</span>
                                </div>
                                <p className="text-[11px] text-txt-dim leading-relaxed">Pixel-perfect cursor tracking. Records any browser tab, auto-imports to editor.</p>
                            </div>
                        </div>

                        {/* Desktop App */}
                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] text-left">
                            <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/[0.06] flex items-center justify-center shrink-0">
                                <span className="text-base leading-none">🖥️</span>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-white">Desktop App</span>
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/8 text-txt-dim font-medium">SOON</span>
                                </div>
                                <p className="text-[11px] text-txt-dim leading-relaxed">Windows app with global cursor hooks, system audio, and whole-screen capture.</p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </main>
            </div>

            {/* Footer */}
            <footer className="relative mt-24 pb-12 pt-8 w-full max-w-none px-8 md:px-12 xl:px-16 border-t border-white/5 text-txt-dim text-xs">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    {/* Brand Info */}
                    <div className="flex items-center gap-3">
                        <img src="/logo.svg" alt="ScreenRecord.ing" className="w-6 h-6" />
                        <span className="text-sm font-semibold text-white">ScreenRecord<span className="text-brand-400">.ing</span></span>
                        <span className="text-txt-dim">• &copy; {new Date().getFullYear()} Free & Open • Icons by <a href="https://hugeicons.com" target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors">Hugeicons</a> & <a href="https://iconsax.io" target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors">Iconsax</a></span>
                    </div>

                    {/* Bottom-to-Top Opener Footer Links */}
                    <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-center">
                        <button
                            onClick={() => setIsFaqOpen(true)}
                            className="px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-txt-secondary hover:text-white transition-all border border-white/5 flex items-center gap-1.5 font-medium"
                        >
                            <span>❓ FAQs</span>
                        </button>
                        <button
                            onClick={() => { setFeedbackTab('feedback'); setIsFeedbackOpen(true) }}
                            className="px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-txt-secondary hover:text-white transition-all border border-white/5 flex items-center gap-1.5 font-medium"
                        >
                            <span>💬 Feedback</span>
                        </button>
                        <button
                            onClick={() => { setFeedbackTab('roadmap'); setIsFeedbackOpen(true) }}
                            className="px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-txt-secondary hover:text-white transition-all border border-white/5 flex items-center gap-1.5 font-medium"
                        >
                            <span>🗺️ Roadmap</span>
                        </button>
                        <button
                            onClick={() => { setFeedbackTab('changelog'); setIsFeedbackOpen(true) }}
                            className="px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-txt-secondary hover:text-white transition-all border border-white/5 flex items-center gap-1.5 font-medium"
                        >
                            <span>📜 Changelog</span>
                        </button>
                        <button
                            onClick={() => setIsDonateOpen(true)}
                            className="px-3.5 py-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 transition-all border border-amber-500/20 flex items-center gap-1.5 font-medium"
                        >
                            <span>☕ Donate</span>
                        </button>
                    </div>
                </div>
            </footer>

            {/* Bottom to Top Opener Window Dialogs */}
            <FaqModal
                isOpen={isFaqOpen}
                onClose={() => setIsFaqOpen(false)}
            />

            <FeedbackModal
                isOpen={isFeedbackOpen}
                initialTab={feedbackTab}
                onClose={() => setIsFeedbackOpen(false)}
            />

            <DonateModal
                isOpen={isDonateOpen}
                onClose={() => setIsDonateOpen(false)}
            />

            <ProjectSidebar />
        </div>
    )
}

function FloatingSticker({
    icon,
    text,
    delay = 0,
    initialRotation = 0,
    className = ''
}: {
    icon: any
    text: string
    delay?: number
    initialRotation?: number
    className?: string
}) {
    return (
        <motion.div
            drag
            dragConstraints={{ left: -160, right: 160, top: -90, bottom: 90 }}
            dragElastic={0.25}
            dragTransition={{ bounceStiffness: 300, bounceDamping: 20 }}
            initial={{ opacity: 0, y: 20, rotate: initialRotation }}
            animate={{
                opacity: 1,
                y: [-6, 6, -6],
                rotate: [initialRotation - 1, initialRotation + 1.5, initialRotation - 1]
            }}
            transition={{
                y: { duration: 3.8, repeat: Infinity, ease: 'easeInOut', delay },
                rotate: { duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay },
                opacity: { duration: 0.5, delay: 0.6 }
            }}
            whileHover={{ scale: 1.08, rotate: 0 }}
            whileDrag={{ scale: 1.15, cursor: 'grabbing', zIndex: 50 }}
            className={`cursor-grab select-none w-36 h-32 p-4 rounded-2xl bg-[#0A0A0C] border border-white/10 hover:border-brand-500/40 shadow-2xl flex flex-col items-center justify-center gap-3 transition-colors group ${className}`}
        >
            <div className="p-2.5 rounded-xl bg-brand-500/10 border border-brand-500/20 text-brand-400 group-hover:scale-110 transition-transform">
                {icon}
            </div>
            <span className="text-xs font-semibold text-white tracking-wide text-center">{text}</span>
        </motion.div>
    )
}
